<template>
	<layout-page :title="__('Price Ticker')">
		<price-indications-filter
			v-model="filter"
			v-if="showFilter"
			v-on:close="showFilter = false"
		/>
		<div class="flex-grow flex flex-col">
			<!-- header -->
			<div
				class="bg-green px-4 sm:px-6 py-4 md:rounded-tl-2xl md:rounded-tl-2xl xl:rounded-t-2xl flex justify-start items-center"
			>
				<h1 class="text-2xl md:text-4xl font-semibold text-white">
					{{ __("Price Ticker") }}
				</h1>
			</div>

			<!-- filter bar -->
			<div
				class="px-4 sm:px-6 lg:px-6 py-4 bg-gray-700 flex-wrap flex items-center gap-x-6 md:gap-x-16 justify-between md:justify-start"
			>
				<button
					class="relative inline-flex items-center px-2 lg:pr-4 py-1.5 font-medium text-white bg-gray-500 hover:bg-gray-400 rounded-lg flex items-center focus:ring-2 ring-gray-400"
					v-on:click="showFilter = !showFilter"
				>
					<ui-icon
						class="mr-2 flex-shrink-0 h-5 w-5"
						name="filter"
						stroke-width="2"
					/>
					{{ __("Filter") }}
					<span
						class="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-or-50 transform translate-x-1/4 -translate-y-1/2 bg-orange rounded-full"
						v-if="activeFilterCount > 0"
						>{{ activeFilterCount }}</span
					>
				</button>

				<div class="flex gap-x-4 gap-y-3 text-gray-100">
					<div class="flex items-center">
						<ui-radio-group
							class="col-span-2"
							:key="'ALLOFFERS-' + filter.bestOffers"
							v-model="filter.bestOffers"
							radioValue="0"
							name="bestOffers"
							label="All prices"
							:is-white="true"
						></ui-radio-group>
					</div>
					<div class="flex items-center cursor-pointer">
						<ui-radio-group
							class="col-span-2"
							:key="'BESTOFFERS-' + filter.bestOffers"
							v-model="filter.bestOffers"
							radioValue="1"
							name="bestOffers"
							label="Only best prices"
							:is-white="true"
						></ui-radio-group>
					</div>
				</div>
			</div>

			<content-price-indications-empty
				v-if="priceIndications.length < 1"
				:user="user"
			/>

			<price-indications-best-view
				:user="user"
				:priceIndications="filteredPriceIndications"
				:filter="filter"
				:serviceAvailable="serviceAvailable"
				v-else
			/>
		</div>
		<div v-if="!updatedAt"></div>
	</layout-page>
</template>

<script>
let options = require("../cropOptions");
import UiFilterSessionStorage from "../components/Ui/FilterSessionStorage.vue";
import axios from "axios";

export default {
	mixins: [UiFilterSessionStorage],
	props: {
		user: {
			type: Object,
			required: true,
		},
		showFavouriteProductsModal: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		let productGrades = [];
		this.user.favorite_product_types
			.map((t) => options.productGradeOptions[t])
			.flat()
			.forEach((option) => {
				if (option.id.includes("WHEAT_B") && option.default) {
					productGrades.push("WHEAT_B");
				} else if (!option.id.includes("WHEAT_B")) {
					productGrades.push(option.id);
				}
			});

		return {
			viewType: "best",
			showFilter: false,
			filter: Vue.observable({
				productGrades: productGrades,
				productYears: [],
				proteinValues: [],
				bestOffers: 0,
				dateType: [],
				monthType: {
					month_from: null,
					month_to: null,
				},
			}),
			interval: null,
			delay: 1000 * 60,
			// last price indication date
			renderedAt: this.$page.props.rendered_at,
			serviceAvailable: this.$page.props.serviceAvailable,
			priceIndications: this.$page.props.priceIndications,
		};
	},
	computed: {
		updatedAt: {
			get() {
				return this.renderedAt;
			},
			set(newValue) {
				this.renderedAt = newValue;
			},
		},
		activeFilterCount() {
			return Object.values(this.filter).flat().length - 2;
		},
		filteredPriceIndications() {
			let selectedGrades = this.filter.productGrades,
				selectedHarvests = this.filter.productYears,
				selectedDates = this.filter.dateType,
				selectedProteins = this.filter.proteinValues;

			let filter = this.priceIndications
				.filter(
					(priceIndication) =>
						selectedHarvests.length == 0 ||
						selectedHarvests.includes(priceIndication.offer.product__harvest)
				)
				.filter((priceIndication) => {
					if (priceIndication.offer.product__grade) {
						if (!priceIndication.offer.product__grade.includes("WHEAT_B")) {
							return true;
						}

						let exists = [];
						if (selectedProteins.length > 0) {
							selectedProteins.every((protein) => {
								const proteinDotReplaced = protein.replace(".", "");
								exists.push(
									priceIndication.offer.product__grade.includes(
										proteinDotReplaced
									)
								);
								// Schleife abbrechen sobald true enthalten ist
								return !exists.includes(true);
							});
						}

						if (selectedProteins.length > 0) {
							return exists.includes(true);
						}
					}

					return true;
				})
				.filter(
					(priceIndication) =>
						selectedDates.length == 0 ||
						(selectedDates.includes("AT_HARVEST") &&
							priceIndication.offer.date__type == "AT_HARVEST") ||
						(selectedDates.includes("MONTH") &&
							priceIndication.offer.date__type != "AT_HARVEST" &&
							this.intervalCheck(
								priceIndication.offer.date__month_from,
								priceIndication.offer.date__month_to
							))
				)
				.filter((priceIndication) => {
					let exists = [];
					if (
						selectedGrades.length > 0 &&
						priceIndication.offer.product__grade
					) {
						selectedGrades.every((grade) => {
							exists.push(priceIndication.offer.product__grade.includes(grade));
							// Schleife abbrechen sobald true enthalten ist
							return !exists.includes(true);
						});
					}

					if (selectedGrades.length > 0) {
						return exists.includes(true);
					}

					return true;
				});

			return filter;
		},
	},
	mounted() {
		if (this.showFavouriteProductsModal) {
			this.$bus.$emit(
				"toggle-iframe-modal",
				this.$route("user.select_favourite_products", { iframe_modal: true }),
				false
			);
		}

		this.interval = setInterval(this.load, this.delay);

		this.$bus.$off("iframe close");
		this.$bus.$on("iframe close", this.iframeEventListener);
	},
	beforeDestroy() {
		clearInterval(this.interval);
		this.interval = null;
	},
	methods: {
		load() {
			const data = {
				updated_at: this.updatedAt,
				service_available: this.serviceAvailable,
			};

			let promise = new Promise((resolve, reject) => {
				const response = axios.post(
					this.$route("price_indications.watch"),
					data
				);

				resolve(response);
				reject(response);
			});

			promise.then(
				(result) => this.onSuccess(result),
				(error) => this.onError(error)
			);
		},
		onSuccess(result) {
			if (result.data.updated_at != undefined) {
				this.updatedAt = result.data.updated_at;
			}

			if (result.data.serviceAvailable != undefined) {
				if (this.serviceAvailable != result.data.serviceAvailable) {
					this.serviceAvailable = result.data.serviceAvailable;
				}
			}

			if (result.data.priceIndications.length > 0) {
				this.priceIndications = result.data.priceIndications;
			}
		},
		onError(error) {
			if (this.interval) {
				clearInterval(this.interval);
				this.interval = null;
			}

			window.location.reload();
		},
		intervalCheck(month_from, month_to) {
			let selectedMonthFrom = this.filter.monthType.month_from,
				selectedMonthTo = this.filter.monthType.month_to,
				leftCheck =
					selectedMonthFrom == null ||
					month_from == null ||
					month_from >= selectedMonthFrom,
				rightCheck = selectedMonthTo == null || selectedMonthTo >= month_to;
			return leftCheck && rightCheck;
		},
		iframeEventListener() {
			console.log("RECEIVE", "iframe close");
			this.iframeClosed = true;
		},
	},
};
</script>
